import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SIGNUP_URL } from '../config';

const TABS = [
  {
    label: 'Sales',
    headline: 'See every sale as it happens.',
    desc: 'Every transaction from POS appears in Business. Totals by payment type, cashier, and period — including what is still owed.',
  },
  {
    label: 'Stock',
    headline: 'Products and stock when you need it.',
    desc: 'Add what you sell with name and price. When you track stock, every sale can reduce quantity. Levels, movements, and low-stock signals stay in one place.',
  },
  {
    label: 'Buying',
    headline: 'Buy from suppliers without losing the trail.',
    desc: 'Suppliers, purchase orders, and receiving goods. Stock increases when you receive. What you owe appears in Money so you can pay and close.',
  },
  {
    label: 'Money',
    headline: 'Know what came in, what went out, and what you made.',
    desc: 'Money in and out, customers who owe you, bills to pay, expenses, and reports. Profitability uses revenue, product cost, and operating expenses — not cash alone.',
  },
  {
    label: 'Registers',
    headline: 'Control every register session.',
    desc: 'Open and close POS sessions, opening float, and expected cash — attributed to the register and cashier.',
  },
  {
    label: 'Team',
    headline: 'Manage who can see and do what.',
    desc: 'Role-based access. Cashiers sell on POS. Owners and managers run the business in Business.',
  },
];

export function BusinessPage() {
  const [tab, setTab] = useState(0);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === '#inventory' || hash === '#stock') setTab(1);
    if (hash === '#buying') setTab(2);
    if (hash === '#money' || hash === '#reports') setTab(3);
  }, [hash]);

  return (
    <>
      <section className="px-6 pt-28 pb-16 lg:px-10 lg:pt-36 lg:pb-20">
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand/15 bg-brand-light px-3 py-1.5 text-[0.72rem] font-bold tracking-widest text-brand uppercase">
            cevop Business
          </div>
          <h1 className="font-display mb-5 max-w-[20ch] text-[2.75rem] leading-[1.06] font-extrabold tracking-tight text-ink lg:text-[3.625rem]">
            Run and understand the business — not another spreadsheet
          </h1>
          <p className="mb-8 max-w-[480px] text-[1.0625rem] leading-[1.7] text-ink-2">
            The Business workspace brings Home, Stock, Sales, Buying, Money, Registers, and Settings together. Sell
            quickly in POS. See what it means here — from the same books.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={SIGNUP_URL}
              className="inline-flex items-center justify-center rounded-xl bg-brand px-7 py-3.5 text-[0.9375rem] font-semibold text-brand-on shadow-sm hover:bg-brand-hover"
            >
              Start free
            </a>
            <Link
              to="/pos"
              className="inline-flex items-center justify-center rounded-xl border border-line-strong px-7 py-3.5 text-[0.9375rem] font-semibold text-ink hover:bg-surface-2"
            >
              See the POS first
            </Link>
          </div>
        </div>
      </section>

      <section id="stock" className="scroll-mt-24 border-y border-line bg-surface-2/60 px-6 py-20 lg:px-10 lg:py-28">
        <div id="inventory" className="sr-only" />
        <div id="buying" className="sr-only" />
        <div id="money" className="sr-only" />
        <div id="reports" className="sr-only" />
        <div className="mx-auto max-w-[1280px]">
          <h2 className="font-display mb-8 text-[2rem] font-extrabold text-ink">Operating areas behind the counter.</h2>
          <div className="mb-8 flex gap-1.5 overflow-x-auto pb-2">
            {TABS.map((t, i) => (
              <button
                key={t.label}
                type="button"
                onClick={() => setTab(i)}
                className={`flex-shrink-0 rounded-xl px-4 py-2.5 text-[0.85rem] font-semibold whitespace-nowrap ${
                  tab === i
                    ? 'bg-inverse text-inverse-fg shadow-sm'
                    : 'border border-line bg-panel text-ink-2'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div key={TABS[tab].label} className="mk-panel">
            <h3 className="font-display mb-3 text-[1.5rem] font-extrabold text-ink">{TABS[tab].headline}</h3>
            <p className="text-[1rem] leading-[1.7] text-ink-3">{TABS[tab].desc}</p>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-10 lg:py-28">
        <div className="mx-auto max-w-[700px] text-center">
          <h2 className="font-display mb-4 text-[2rem] font-extrabold text-ink">Your numbers should agree.</h2>
          <p className="mb-8 text-[1rem] text-ink-3">
            A POS charge, a received order, an expense — each posts to the same chart of accounts. Reports read from
            that ledger: revenue, product cost, operating expenses, and net profit.
          </p>
          <Link to="/help/accounting" className="font-semibold text-brand hover:underline">
            See the default chart of accounts →
          </Link>
        </div>
      </section>

      <section className="border-t border-line bg-surface-2/60 px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[640px] text-center">
          <h2 className="font-display mb-5 text-[2.25rem] font-extrabold text-ink">Manage the business. Know the numbers.</h2>
          <a
            href={SIGNUP_URL}
            className="inline-flex items-center justify-center rounded-xl bg-brand px-8 py-3.5 text-[0.9375rem] font-semibold text-brand-on hover:bg-brand-hover"
          >
            Start free
          </a>
        </div>
      </section>
    </>
  );
}
